import type {Card,Snapshot} from '@platform/contracts/x';
import {applyRevisionPatch,type SnapshotPatch} from './revisionPatch';
type Layout=[string,string,Card['rows'],string|null,string|null];
type State=Omit<Snapshot,'cards'|'upcoming'>&{cards:[number,number,number|null][];upcoming:State|null;catalogOffset?:number;catalogCount?:number;cardPurchases?:number[]};
export type XSnapshot={v:5;catalog:Layout[];state:State};
export function applyXPatch(current:XSnapshot|null,patch:SnapshotPatch):XSnapshot|null{
 const state=applyRevisionPatch(current?.state??null,patch,current?.v??5);
 return current&&state?{...current,state}:null;
}
// A delayed display flush may share the current domain revision. It cannot
// touch money, ownership, balls, deadlines, awards, or the revision itself.
export function applyXMetrics(current:XSnapshot|null,patch:SnapshotPatch):XSnapshot|null{
 if(!current||patch.d!==current.state.drawId||patch.r!==patch.b)return null;
 for(const change of patch.c){
  const original=patch.p[change[0] as number];if(!original)return null;
  const path=[...original];let draw=current.state;
  if(path[0]==='upcoming'){if(!draw.upcoming)return null;draw=draw.upcoming;path.shift();}
  if(path.join('/')==='purchaseCount'||path.join('/')==='cardPurchases'||path.join('/')==='cardPurchases/*')continue;
  if(path.join('/')!=='prizes/*/amountMinor'||(change.length-1)%2!==0)return null;
  for(let i=1;i<change.length;i+=2)if(draw.prizes[change[i] as number]?.id!=='jackpot')return null;
 }
 const state=applyRevisionPatch(current.state,patch,current.v,true);
 return state?{...current,state}:null;
}
export function unpackX(packed:XSnapshot):Snapshot|null{
 if(packed.v!==5||!Array.isArray(packed.catalog))return null;
 try{
  const slices=new Map<number,{count:number;hash:string}>();
  const hashes=new Map<string,number>();
  const draw=(state:State):Snapshot=>{
   if(state.x.modelVersion!=='fixed-multiplier-v1')throw Error('model');
   const offset=state.catalogOffset??0;
   const count=state.catalogCount;
   if(count!==120||!Number.isInteger(offset)||offset<0||offset%120!==0||offset+count>packed.catalog.length)throw Error('catalog');
   const known=slices.get(offset),knownOffset=hashes.get(state.x.catalogHash);
   if(known&&(known.count!==count||known.hash!==state.x.catalogHash)||knownOffset!==undefined&&knownOffset!==offset)throw Error('catalog identity');
   slices.set(offset,{count,hash:state.x.catalogHash});hashes.set(state.x.catalogHash,offset);
   const layout=packed.catalog.slice(offset,offset+count);
   if(new Set(layout.map(c=>c[0])).size!==count||new Set(layout.map(c=>c[1])).size!==1)throw Error('catalog identity');
   const balls=new Set(state.balls.map(b=>b.number)),own=new Map(state.cards.map(([i,stake,jp])=>[i,{stake,jp}]));
   const stage=state.tombala.nextStageId,multipliers=state.x.multipliers;
   if(multipliers.length!==3||multipliers.some(n=>!Number.isFinite(n)||n<=0)||own.size!==state.cards.length||state.cards.some(([i,a,jp])=>!Number.isInteger(i)||i<offset||i>=offset+count||!Number.isSafeInteger(a)||a<=0||jp!==null&&(!Number.isSafeInteger(jp)||jp<0)))throw Error('invalid state');
   if(state.cardPurchases&&(state.cardPurchases.length!==count||state.cardPurchases.some(n=>!Number.isSafeInteger(n)||n<0)))throw Error('invalid card purchase counts');
   const cards:Card[]=layout.map(([id,version,rows,name,colorGroup],index)=>{
    const remaining=rows.map(r=>r.filter(n=>n!==null&&!balls.has(n)).length).sort((a,b)=>a-b);
    const stake=own.get(offset+index)?.stake??0,jp=own.get(offset+index)?.jp;
    const values=multipliers.map(m=>Math.round(stake*m));
    if(values.some(n=>!Number.isSafeInteger(n)))throw Error('unsafe money');
    return {id,version,rows,name:name??undefined,colorGroup:colorGroup??undefined,ownMinor:stake,purchaseCount:state.cardPurchases?.[index],
     matched:15-remaining.reduce((a,b)=>a+b,0),completedRows:remaining.filter(n=>n===0).length,
     numbersToNextStage:remaining.slice(0,stage==='one-line'?1:stage==='two-lines'?2:3).reduce((a,b)=>a+b,0),
     prizeShares:{...Object.fromEntries(['one-line','two-lines','full-house'].map((key,i)=>[key,values[i]])),...(jp!=null?{jackpot:jp}:{})}};
   });
   return {...state,cards,upcoming:state.upcoming?draw(state.upcoming):null};
  };
  const result=draw(packed.state);
  let end=0;
  for(const [offset,slice] of [...slices].sort((a,b)=>a[0]-b[0])){if(offset!==end)throw Error('overlapping or missing catalog');end+=slice.count;}
  if(end!==packed.catalog.length)throw Error('unused catalog');
  return result;
 }catch{return null;}
}
